/**
 * Andar Bahar — server-authoritative rules engine (2–6 players against the house, session game with chips).
 *
 * Rules implemented (toggles live in {@link AndarBaharConfig}):
 *  - One 52-card deck. A card — the Joker — is turned up. Each player bets on Andar ("inside") or Bahar ("outside")
 *    in seat order, or sits the round out.
 *  - Cards are then dealt face up one at a time, alternately to Andar and Bahar, Andar first, until a card of the
 *    Joker's rank appears. The side it lands on wins.
 *  - A winning Andar bet is paid `andarPayout`% of the stake (90% by default) and a winning Bahar bet `baharPayout`%
 *    (100%): Andar is dealt first and so wins slightly more often, which the lower payout balances. The stake is
 *    returned with the winnings; a losing bet is lost.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';

export type AndarBaharSide = 'ANDAR' | 'BAHAR';

export interface AndarBaharConfig {
  minBet: number;
  maxBet: number;
  minPlayers: number;
  maxPlayers: number;
  /** Winnings on a winning Andar bet, as a percentage of the stake. */
  andarPayout: number;
  baharPayout: number;
  startDelayMs: number;
  dealDelayMs: number;
  handEndDelayMs: number;
}

export type AndarBaharPhase = 'WAITING' | 'BETTING' | 'DEALING' | 'HAND_END';
export type AndarBaharSeatStatus = 'WAITING' | 'BET' | 'SIT_OUT';

export interface AndarBaharPlayer {
  playerId: string;
  chips: number;
  status: AndarBaharSeatStatus;
  side: AndarBaharSide | null;
  bet: number;
  /** What came back to the player at the end of the hand (stake plus winnings, or 0). */
  payout: number;
}

export interface AndarBaharResult {
  handNo: number;
  joker: CardId;
  winner: AndarBaharSide;
  andar: CardId[];
  bahar: CardId[];
  seats: { seat: number; side: AndarBaharSide; bet: number; payout: number; delta: number }[];
}

export interface AndarBaharState {
  config: AndarBaharConfig;
  seats: (AndarBaharPlayer | null)[];
  phase: AndarBaharPhase;
  handNo: number;
  /** Seat to bet. */
  turn: number;
  joker: CardId | null;
  deck: CardId[];
  andar: CardId[];
  bahar: CardId[];
  winner: AndarBaharSide | null;
  lastResult: AndarBaharResult | null;
  handsPlayed: number;
}

export type AndarBaharAction = { type: 'bet'; side: AndarBaharSide; amount: number } | { type: 'sitOut' };

export type AndarBaharLegal = { kind: 'bet'; min: number; max: number; canSitOut: boolean } | { kind: 'none' };

export interface AndarBaharView {
  gameType: 'ANDAR_BAHAR';
  config: AndarBaharConfig;
  seat: number | null;
  phase: AndarBaharPhase;
  handNo: number;
  turn: number;
  joker: CardId | null;
  andar: CardId[];
  bahar: CardId[];
  winner: AndarBaharSide | null;
  deckCount: number;
  players: ({ seat: number; playerId: string; chips: number; status: AndarBaharSeatStatus; side: AndarBaharSide | null; bet: number; payout: number } | null)[];
  lastResult: AndarBaharResult | null;
}

const DEFAULTS: AndarBaharConfig = {
  minBet: 10,
  maxBet: 1000,
  minPlayers: 2,
  maxPlayers: 6,
  andarPayout: 90,
  baharPayout: 100,
  startDelayMs: 2000,
  dealDelayMs: 650,
  handEndDelayMs: 4500,
};

function seatsWhere(s: AndarBaharState, pred: (p: AndarBaharPlayer, i: number) => boolean): number[] {
  const out: number[] = [];
  s.seats.forEach((p, i) => {
    if (p && pred(p, i)) out.push(i);
  });
  return out;
}

const canStart = (s: AndarBaharState): boolean => seatsWhere(s, (p) => p.chips >= s.config.minBet).length >= s.config.minPlayers;

function advanceBetting(s: AndarBaharState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'WAITING') {
      s.turn = seat;
      return;
    }
  }
  s.phase = seatsWhere(s, (p) => p.status === 'BET').length > 0 ? 'DEALING' : 'WAITING';
}

function startHand(s: AndarBaharState, rng: Rng, events: EngineEvent[]): void {
  s.handNo++;
  s.deck = shuffle(buildDeck(), rng);
  s.joker = s.deck.pop()!;
  s.andar = [];
  s.bahar = [];
  s.winner = null;
  for (const p of s.seats) {
    if (!p) continue;
    p.side = null;
    p.bet = 0;
    p.payout = 0;
    p.status = p.chips >= s.config.minBet ? 'WAITING' : 'SIT_OUT';
  }
  s.phase = 'BETTING';
  s.turn = -1;
  events.push({ type: 'handStart', handNo: s.handNo, joker: s.joker });
  advanceBetting(s, -1);
  if (s.phase !== 'BETTING') events.push({ type: 'waitingForPlayers' });
}

function settle(s: AndarBaharState, events: EngineEvent[]): void {
  const winner = s.winner!;
  const pct = winner === 'ANDAR' ? s.config.andarPayout : s.config.baharPayout;
  const seats: AndarBaharResult['seats'] = [];
  s.seats.forEach((p, seat) => {
    if (!p || p.status !== 'BET' || p.side === null) return;
    p.payout = p.side === winner ? p.bet + Math.floor((p.bet * pct) / 100) : 0;
    p.chips += p.payout;
    seats.push({ seat, side: p.side, bet: p.bet, payout: p.payout, delta: p.payout - p.bet });
  });
  s.lastResult = { handNo: s.handNo, joker: s.joker!, winner, andar: [...s.andar], bahar: [...s.bahar], seats };
  s.handsPlayed++;
  s.phase = 'HAND_END';
  events.push({ type: 'handEnd', handNo: s.handNo, winner, results: seats.map((r) => ({ seat: r.seat, delta: r.delta })) });
}

export const andarBaharEngine: GameEngine<AndarBaharConfig, AndarBaharState, AndarBaharAction, AndarBaharView, AndarBaharLegal> = {
  gameType: 'ANDAR_BAHAR',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxPlayers', c.maxPlayers, 1, 8);
    checkInt('minPlayers', c.minPlayers, 1, c.maxPlayers);
    checkInt('minBet', c.minBet, 1, 1e9);
    checkInt('maxBet', c.maxBet, c.minBet, 1e12);
    checkInt('andarPayout', c.andarPayout, 50, 100);
    checkInt('baharPayout', c.baharPayout, 50, 100);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    checkInt('dealDelayMs', c.dealDelayMs, 0, 60000);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats) {
    const s: AndarBaharState = {
      config,
      seats: Array<AndarBaharPlayer | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      turn: -1,
      joker: null,
      deck: [],
      andar: [],
      bahar: [],
      winner: null,
      lastResult: null,
      handsPlayed: 0,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    // While bets are still being taken a later seat can join in; once the cards are coming they wait.
    const open = s.phase === 'WAITING' || s.phase === 'HAND_END' || (s.phase === 'BETTING' && chips >= s.config.minBet);
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: open ? 'WAITING' : 'SIT_OUT', side: null, bet: 0, payout: 0 };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    let chips = p.chips;
    const wasTurn = s.turn === seat;
    if (s.phase === 'BETTING') {
      // Nothing is dealt yet: the bet comes back.
      chips += p.bet;
      s.seats[seat] = null;
      if (wasTurn) advanceBetting(s, seat);
    } else {
      // Leaving once the cards are coming forfeits the bet on the table.
      s.seats[seat] = null;
    }
    return { chips, events: [{ type: 'playerLeft', seat }] };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'sitOut') return { type: 'sitOut' };
    if (raw.type === 'bet' && (raw.side === 'ANDAR' || raw.side === 'BAHAR') && typeof raw.amount === 'number' && Number.isInteger(raw.amount)) {
      return { type: 'bet', side: raw.side, amount: raw.amount };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Andar Bahar action');
  },

  applyAction(s, seat, action) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'You are not at this table');
    assertEngine(s.phase === 'BETTING', 'INVALID_PHASE', 'Betting is closed');
    assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    if (action.type === 'sitOut') {
      p.status = 'SIT_OUT';
      events.push({ type: 'sitOut', seat });
      advanceBetting(s, seat);
      return events;
    }
    const max = Math.min(s.config.maxBet, p.chips);
    assertEngine(action.amount >= s.config.minBet && action.amount <= max, 'INVALID_AMOUNT', `Bet between ${s.config.minBet} and ${max}`);
    p.chips -= action.amount;
    p.bet = action.amount;
    p.side = action.side;
    p.status = 'BET';
    events.push({ type: 'bet', seat, side: action.side, amount: action.amount });
    advanceBetting(s, seat);
    return events;
  },

  getActors(s) {
    return s.phase === 'BETTING' && s.turn >= 0 && s.seats[s.turn] ? [s.turn] : [];
  },

  getLegalActions(s, seat) {
    if (s.phase !== 'BETTING' || seat !== s.turn || !s.seats[seat]) return { kind: 'none' };
    return { kind: 'bet', min: s.config.minBet, max: Math.min(s.config.maxBet, s.seats[seat]!.chips), canSitOut: true };
  },

  getTimeoutAction() {
    return { type: 'sitOut' };
  },

  getView(s, seat) {
    return {
      gameType: 'ANDAR_BAHAR',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      turn: s.turn,
      joker: s.joker,
      andar: [...s.andar],
      bahar: [...s.bahar],
      winner: s.winner,
      deckCount: s.deck.length,
      players: s.seats.map((p, i) => (p ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, side: p.side, bet: p.bet, payout: p.payout } : null)),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'DEALING') return { kind: 'deal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DEALING') {
      // One card per step so the table can show the deal as it happens.
      const card = s.deck.pop()!;
      const side: AndarBaharSide = (s.andar.length + s.bahar.length) % 2 === 0 ? 'ANDAR' : 'BAHAR';
      (side === 'ANDAR' ? s.andar : s.bahar).push(card);
      events.push({ type: 'cardDealt', side, card });
      if (parseCard(card).rank === parseCard(s.joker!).rank) {
        s.winner = side;
        events.push({ type: 'winner', side });
        settle(s, events);
      }
      return events;
    }
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DEALING',
  getChips: (s) => Object.fromEntries(seatsWhere(s, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => s.handsPlayed,
};
